import React from 'react';

import { Box, Grid, Button, Typography, Skeleton, useMediaQuery, useTheme, SxProps } from '@mui/material';

import { Maybe } from '@/types';
import { BlogDetailsPage } from '@/types/generated';

import BlogListItem, { ICardDesignType } from '../BlogListItem';
import BlogListPagination from '../BlogListPagination/BlogListPagination';
import BlogCarousel from '..//BlogCarousel';

import {
	blogListContainerStyles,
	getBlogListItemContainerStyles,
	showMoreBoxStyles,
} from './BlogListPageDefault.styles';

export interface IBlogListPageDefaultProps {
	handleShowMoreClick: () => void;
	totalBlogs: number;
	displayShowMoreButton: boolean;
	blogsList: Array<BlogDetailsPage>;
	buttonLabel: string;
	loadMoreButtonLabel: string;
	countAcrossDesktop?: Maybe<number>;
	countAcrossMobile?: Maybe<number>;
	hasPagination?: Maybe<boolean>;
	handlePaginationChangeEvent: (e: React.ChangeEvent<unknown>, page: number) => void;
	limit?: Maybe<number>;
	cardDesignType?: ICardDesignType;
	displayType?: Maybe<string>;
	isLoading?: boolean;
}

const SkeletonItem = ({ itemName }: { itemName: string }) => {
	return (
		<Box data-testid={itemName}>
			<Skeleton variant="rectangular" width="100%" height="15.313rem" />
			<Box sx={{ pt: 0.5 }}>
				<Skeleton />
				<Skeleton width="60%" />
			</Box>
		</Box>
	);
};

const LoadingSkeleton = ({ itemCount }: { itemCount: number }) => {
	return (
		<>
			{Array.from(Array(itemCount), (e, i) => (
				<SkeletonItem itemName={`bloglist-skeleton-item-${i}`} key={i} />
			))}
		</>
	);
};

const BlogListPageDefault = ({
	handleShowMoreClick,
	totalBlogs,
	displayShowMoreButton,
	blogsList,
	buttonLabel,
	loadMoreButtonLabel,
	cardDesignType,
	countAcrossDesktop,
	countAcrossMobile,
	hasPagination,
	limit,
	displayType,
	isLoading = false,
	handlePaginationChangeEvent,
}: IBlogListPageDefaultProps) => {
	const isFeatured = displayType?.toLowerCase() === 'featured';
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const hasBlogs = blogsList.length > 0;
	const showSkeleton = isLoading || !hasBlogs;
	const skeletonCount = isSmallScreen ? countAcrossMobile : countAcrossDesktop;

	const testId = isFeatured ? 'blog_list_items_container_featured' : 'blog_list_items_container';
	return (
		<Grid container sx={blogListContainerStyles(displayType ?? '') as SxProps} data-testid="blog_list_container">
			<Grid
				data-testid={testId}
				container
				sx={getBlogListItemContainerStyles(isSmallScreen, countAcrossDesktop, countAcrossMobile)}
			>
				{showSkeleton && <LoadingSkeleton itemCount={skeletonCount ?? 4} />}
				{hasBlogs &&
					(isSmallScreen && displayType === 'One Row' ? (
						<BlogCarousel
							blogsList={blogsList}
							isSmallScreen={isSmallScreen}
							buttonLabel={buttonLabel}
							cardDesignType={cardDesignType}
							displayType={displayType ?? undefined}
						/>
					) : (
						blogsList?.map((item, index) => {
							const isLastItem = index === blogsList.length - 1;
							return (
								item && (
									<React.Fragment key={item._id}>
										<BlogListItem
											blogDetails={item}
											buttonLabel={buttonLabel}
											key={item._id}
											cardDesignType={cardDesignType}
											displayType={displayType ?? undefined}
										/>
										{!isLastItem && isSmallScreen && displayType === 'Blog List Page' && (
											<Box width={'100%'} height={'1px'} bgcolor={'#2288C9'}></Box> // Example of a divider
										)}
									</React.Fragment>
								)
							);
						})
					))}
			</Grid>
			{!hasPagination && displayShowMoreButton && (
				<Box sx={showMoreBoxStyles} data-testid="blog_list_show_more_container">
					<Typography variant="bodyLargeBook">
						Showing {blogsList.length} of {totalBlogs}
					</Typography>
					<Button
						size="L"
						variant="primaryDefault"
						onClick={handleShowMoreClick}
						data-testid="blog_list_show_more_button"
					>
						{loadMoreButtonLabel}
					</Button>
				</Box>
			)}
			{hasPagination && (
				<Box sx={showMoreBoxStyles} data-testid="blog_list_pagination_container">
					<BlogListPagination
						totalBlogs={totalBlogs}
						countPerPage={limit}
						handlePaginationChangeEvent={handlePaginationChangeEvent}
					/>
				</Box>
			)}
		</Grid>
	);
};

export default BlogListPageDefault;
